import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { d as debounce_1 } from './index4.js';
import { j as VideoType, k as VideoService, S as StringService } from './index2.js';
import { D as DomService, T as TrackingService } from './index3.js';
import { R as Ratio, P as Position, W as WidthGetterAlgorithm, r as ratioValues, a as ratioKeys, p as positionValues, b as positionKeys } from './roche-image.types.js';
import { d as defineCustomElement$2 } from './roche-icon2.js';
import { b as breakpoints, r as remCalc } from './index5.js';
import { A as AdobeTracking } from './index7.js';

const getElementSizes = (element) => {
    const computedStyle = getComputedStyle(element);
    const sizesDefinition = computedStyle.getPropertyValue('--responsive-container')
        || computedStyle.getPropertyValue('-ie-responsive-container');
    return (sizesDefinition || '')
        .replace(/\$/g, '')
        .split(',')
        .filter((size) => size)
        .map((size) => parseInt(size.trim(), 10));
};
const responsive = (element, prop = 'component-width', toObserve = element, callback) => {
    const sizes = getElementSizes(element);
    DomService.createObserver(toObserve, ([{ contentRect: { width } }]) => {
        const size = (sizes.find((item) => item < width) || 0).toString();
        if (element.getAttribute(prop) !== size) {
            element.setAttribute(prop, size);
        }
        if (callback) {
            callback();
        }
    });
};

var YoutubeStates;
(function (YoutubeStates) {
    YoutubeStates[YoutubeStates["UNSTARTED"] = -1] = "UNSTARTED";
    YoutubeStates[YoutubeStates["ENDED"] = 0] = "ENDED";
    YoutubeStates[YoutubeStates["PLAYING"] = 1] = "PLAYING";
    YoutubeStates[YoutubeStates["PAUSED"] = 2] = "PAUSED";
    YoutubeStates[YoutubeStates["BUFFERING"] = 3] = "BUFFERING";
    YoutubeStates[YoutubeStates["VIDEO_CUED"] = 5] = "VIDEO_CUED";
})(YoutubeStates || (YoutubeStates = {}));
var Percentages;
(function (Percentages) {
    Percentages[Percentages["ZERO"] = 0] = "ZERO";
    Percentages[Percentages["TEN"] = 10] = "TEN";
    Percentages[Percentages["TWENTY_FIVE"] = 25] = "TWENTY_FIVE";
    Percentages[Percentages["FIFTY"] = 50] = "FIFTY";
    Percentages[Percentages["SEVENTY_FIVE"] = 75] = "SEVENTY_FIVE";
    Percentages[Percentages["HUNDRED"] = 100] = "HUNDRED";
})(Percentages || (Percentages = {}));

/**
 * detect iOS/iPad/macOS Low Power Mode
 * @async
 * @method detectPowerSavingMode
 * @returns {boolean|undefined} return `undefined` if not sure
 */
async function detectPowerSavingMode() {
    // for iOS/iPadOS Safari, and maybe MacBook macOS Safari (not tested)
    if (/(iP(?:hone|ad|od)|Mac OS X)/.test(navigator.userAgent)) {
        // In Low Power Mode, cumulative delay effect happens on setInterval()
        return new Promise((resolve) => {
            const fps = 60;
            const interval = 1000 / fps;
            const numFrames = 30;
            const startTime = performance.now();
            let i = 0;
            const handle = setInterval(() => {
                if (i < numFrames) {
                    i += 1;
                    return;
                }
                clearInterval(handle);
                const actualInterval = (performance.now() - startTime) / numFrames;
                const ratio = actualInterval / interval; // 1.3x or more in Low Power Mode, 1.1x otherwise
                // alert(actualInterval+' '+interval);
                resolve(ratio > 1.3);
            }, interval);
        });
    }
    return Promise.resolve(false);
}

const rocheVideoPlayerCss = "roche-video-player{display:block;margin-top:3rem;--responsive-container:768px, 1px;-ie-responsive-container:768px, 1px;width:100%}@media (min-width: 90rem){roche-video-player{margin-top:4.5rem}}roche-video-player,roche-video-player *{box-sizing:border-box}roche-video-player .video-player{position:relative}roche-video-player .video-player-container{opacity:0}roche-video-player .video-player-container--show{z-index:250;opacity:1}roche-video-player roche-image{margin-top:0}roche-video-player .content{position:absolute;top:0;left:0;width:100%;height:100%;display:flex;justify-content:center;align-items:center}roche-video-player .overlay{position:absolute;top:0;left:0;width:100%;height:100%;display:flex;justify-content:center;align-items:center}roche-video-player .overlay roche-icon{fill:#fff;border-radius:50%;background:rgba(0, 0, 0, 0.2);box-shadow:0 0 5px 0 rgba(0, 0, 0, 0.3)}roche-video-player .overlay roche-icon:hover{fill:rgba(255, 255, 255, 0.7);border-color:rgba(255, 255, 255, 0.7);cursor:pointer}roche-video-player .overlay--hide{display:none}roche-video-player .close-button{position:absolute;top:1.5rem;right:1.5rem;display:none;margin-right:calc(50% - 50vw);fill:#fff}roche-video-player .close-button:hover{fill:#ccc;cursor:pointer}roche-video-player .close-button--show{z-index:250;display:block}roche-video-player .video-js .vjs-dock-shelf,roche-video-player .video-js .vjs-dock-text{display:none}roche-video-player .video-js .vjs-load-progress div,roche-video-player .video-js .vjs-volume-bar{background-color:#fac9b5}roche-video-player .video-js .vjs-volume-level,roche-video-player .video-js .vjs-play-progress{background-color:var(--color-interactive--primary-idle)}roche-video-player .video-js .vjs-picture-in-picture-control{display:none}roche-video-player .video-js .vjs-duration-display,roche-video-player .video-js .vjs-current-time-display{font-size:0.75rem;color:#fff}roche-video-player .video-js .vjs-time-control{padding-left:2px;padding-right:2px;align-items:center;font-family:\"roche-sans\";font-size:0.75rem}roche-video-player[video-forced-height] .video-player-container,roche-video-player[video-forced-height] .video-player-container *{height:100%}roche-video-player[video-forced-height] video{object-fit:cover}.image--top roche-video-player[video-forced-height] video{object-position:top}.image--center roche-video-player[video-forced-height] video{object-position:center}.image--bottom roche-video-player[video-forced-height] video{object-position:bottom}";
const RocheVideoPlayerStyle0 = rocheVideoPlayerCss;

const RATIO_HD = 16 / 9;
const MINIMUM_VIDEO_PADDING = 36;
const RocheVideoPlayer = /*@__PURE__*/ proxyCustomElement(class RocheVideoPlayer extends HTMLElement {
    handleHide({ detail }) {
        if (detail !== this && this.showVideo && !this.videoloop) {
            this.handlePause();
        }
    }
    get showLargeButton() {
        return this.componentWidth >= breakpoints.medium;
    }
    get video() {
        return this.youtubeVideo || this.brightcoveVideo || undefined;
    }
    get videoType() {
        return this.youtubeVideo ? VideoType.YOUTUBE : VideoType.BRIGHTCOVE;
    }
    get videoOptions() {
        const { brightcoveAccountId, brightcovePlayerId } = DomService.getGlobalConfig();
        return this.videoloop ? Object.assign({ videoId: this.video }, ({
            accountId: brightcoveAccountId,
            playerId: brightcovePlayerId,
            poster: 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=',
            embedOptions: {
                responsive: true,
            },
            options: {
                aspectRatio: '16:9',
                muted: true,
                loop: true,
                autoplay: true,
                controls: false,
                // Fixes IOS – https://player.support.brightcove.com/playback/ios-and-brightcove-player.html
                playsinline: true,
            },
        })) : Object.assign(Object.assign({ videoId: this.video }, (this.videoType === VideoType.YOUTUBE ? {
            width: this.imageElement.getBoundingClientRect().width,
            height: this.imageElement.getBoundingClientRect().height,
        } : {})), (this.videoType === VideoType.BRIGHTCOVE ? {
            accountId: brightcoveAccountId,
            playerId: brightcovePlayerId,
            aspectRatio: '16:9',
            embedOptions: {
                responsive: true,
            },
            options: {
                preload: 'none',
            },
        } : {}));
    }
    get videoPercentage() {
        const currentTime = this.videoType === 'youtube'
            ? Math.round(this.videoObject.getCurrentTime())
            : this.videoObject.player.currentTime();
        const totalVideoLength = this.videoType === 'youtube'
            ? Math.round(this.videoObject.getDuration())
            : this.videoObject.player.duration();
        const videoPercentage = Math.round((currentTime / totalVideoLength) * 100);
        return videoPercentage;
    }
    get videoElement() {
        var _a;
        return (_a = this.videoContainer) === null || _a === void 0 ? void 0 : _a.querySelector('*');
    }
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            event: 'video_tracking',
            component_name: 'roche-video-player',
            additionalParameters: {
                videoId: this.video,
                video_title: this.caption,
                video_percentage: this.videoPercentage,
            },
        };
    }
    constructor() {
        super();
        this.__registerHost();
        this.closedEvent = createEvent(this, "rocheVideoPlayerClose", 7);
        this.timeInterval = null;
        // Stores the percentages that were already send to the Tracking.
        this.trackedVideoPercentages = {};
        /**
          * when a user seeks in a Video(skiping in the timeline) the player pause and play again.
          * these should not be tracked
          */
        this.isSeeking = false;
        this.initialized = null;
        this.entersViewport = (data) => {
            var _a;
            if (data[0].isIntersecting) {
                this.initialized = this.init();
                (_a = this.observer) === null || _a === void 0 ? void 0 : _a.disconnect();
            }
        };
        this.initStatus = 'not initialized';
        this.brightcoveVideoProgressClickEvent = () => {
            var _a;
            const player = (_a = this.videoObject) === null || _a === void 0 ? void 0 : _a.player;
            if (!player)
                return;
            // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
            const progressBarElement = player.playerElIngest_.querySelector('.vjs-progress-holder');
            progressBarElement.addEventListener('click', (e) => {
                const rect = progressBarElement.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = parseFloat((x / rect.width).toFixed(2));
                const videoDuration = player.duration();
                player.currentTime(videoDuration * percentage);
                const playProgressBar = progressBarElement.querySelector('.vjs-play-progress');
                playProgressBar.style.width = `${percentage * 100}%`;
            });
        };
        this.brightcoveVideoEndedEvent = () => {
            var _a;
            const videoObject = (_a = this.videoObject) === null || _a === void 0 ? void 0 : _a.player;
            videoObject === null || videoObject === void 0 ? void 0 : videoObject.on('ended', () => {
                this.track(Object.assign(Object.assign({}, this.defaultTracking), { value: 'end' }));
                AdobeTracking(Object.assign(Object.assign({}, this.defaultAdobeTracking), { progress: 100 }), 'videoComplete');
            });
        };
        this.onYoutubePlayerStateChange = (event) => {
            this.youtubePlayerState = event.data;
            if (this.youtubePlayerState === YoutubeStates.PLAYING) {
                this.timeInterval = setInterval(this.youtubeVideoTimeUpdateEvent, 100);
                this.track(Object.assign(Object.assign({}, this.defaultTracking), { value: 'play' }));
                if (!this.videoloop) {
                    AdobeTracking(Object.assign(Object.assign({}, this.defaultAdobeTracking), { autoplay: this.videoloop ? this.videoloop : false }), 'videoStart');
                }
            }
            else if (this.youtubePlayerState === YoutubeStates.PAUSED
                || this.youtubePlayerState === YoutubeStates.ENDED) {
                /* this else if prevents the racing condition between
                  this function and the setInterval called function */
                clearInterval(this.timeInterval);
            }
            if (this.youtubePlayerState === YoutubeStates.ENDED && !this.videoloop) {
                AdobeTracking(Object.assign(Object.assign({}, this.defaultAdobeTracking), { progress: 100 }), 'videoComplete');
            }
        };
        this.youtubeVideoTimeUpdateEvent = () => {
            if (this.youtubePlayerState === YoutubeStates.PAUSED
                || this.youtubePlayerState === YoutubeStates.ENDED) {
                clearInterval(this.timeInterval);
                this.track(Object.assign(Object.assign({}, this.defaultTracking), { value: 'pause' }));
            }
            this.declareTrackingVideoPercentage(this.videoPercentage);
        };
        this.declareTrackingVideoPercentage = (videoPercentage) => {
            if (videoPercentage < Percentages.TEN) {
                return null;
            }
            if (videoPercentage < Percentages.TWENTY_FIVE) {
                return this.trackVideoPercentage(Percentages.TEN);
            }
            if (videoPercentage < Percentages.FIFTY) {
                return this.trackVideoPercentage(Percentages.TWENTY_FIVE);
            }
            if (videoPercentage < Percentages.SEVENTY_FIVE) {
                return this.trackVideoPercentage(Percentages.FIFTY);
            }
            if (videoPercentage < Percentages.HUNDRED) {
                return this.trackVideoPercentage(Percentages.SEVENTY_FIVE);
            }
            return this.trackVideoPercentage(Percentages.HUNDRED);
        };
        this.preview = undefined;
        this.youtubeVideo = undefined;
        this.brightcoveVideo = undefined;
        this.videoloop = undefined;
        this.caption = undefined;
        this.copyright = undefined;
        this.fullscreen = false;
        this.mobileFullscreenPreview = false;
        this.componentWidth = undefined;
        this.videoReady = undefined;
        this.videoForcedHeight = undefined;
        this.showVideo = false;
        this.currentlyFullscreen = false;
        this.handlePlay = this.handlePlay.bind(this);
        this.handlePause = this.handlePause.bind(this);
        this.handleResize = debounce_1.debounce(this.handleResize.bind(this), 50, false);
    }
    async componentDidLoad() {
        responsive(this.element, 'component-width', this.element, this.handleResize);
        if (this.videoloop) {
            this.handlePlay();
        }
        this.observer = new IntersectionObserver(this.entersViewport.bind(this), { threshold: [0] });
        this.observer.observe(this.element);
    }
    timeout(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    async init() {
        var _a;
        this.initStatus = 'initializing';
        if (!this.initialized) {
            await VideoService.init(this.videoType);
            const powerSavingMode = await detectPowerSavingMode();
            const options = this.videoOptions;
            // remove empty poster for power saving mode, iOS not playing the video in power saving mode
            // this will default to brightcove poster
            if (powerSavingMode && options.poster) {
                delete options.poster;
            }
            try {
                this.videoObject = await VideoService.createPlayer(this.videoType, this.videoElement, options, this.onYoutubePlayerStateChange, this.handleYoutubeError.bind(this));
                // Video Events for Brightcove
                if (this.videoType === VideoType.BRIGHTCOVE) {
                    const isInsideSlider = !!this.element.closest('roche-slider-item');
                    if (isInsideSlider) {
                        this.brightcoveVideoProgressClickEvent();
                    }
                    this.brightcoveVideoEndedEvent();
                    this.brightcoveVideoTimeupdateEvent();
                    this.brightcoveVideoPauseEvent();
                    this.brightcoveVideoPlayEvent();
                    const player = (_a = this.videoObject) === null || _a === void 0 ? void 0 : _a.player;
                    player === null || player === void 0 ? void 0 : player.on('error', (error) => {
                        this.handleBrightcoveError(error);
                    });
                }
                this.handleResize();
            }
            catch (error) {
                this.trackVideoError('VIDEO_INIT_FAILED');
            }
            this.initStatus = 'done';
        }
        return true;
    }
    render() {
        const containerClasses = {
            'video-player-container': true,
            'video-player-container--show': this.showVideo,
        };
        const overlayClasses = {
            overlay: true,
            'overlay--hide': this.showVideo,
        };
        const closeClasses = {
            'close-button': true,
            'close-button--show': this.currentlyFullscreen && this.showVideo,
        };
        const iconHeight = `${remCalc(this.showLargeButton ? 80 : 64)}rem`;
        return (h("div", { key: 'fd14e95aee5788af7821f1cc206b81df988d88c2', class: "video-player" }, h("roche-image", { key: 'cc910eb42def87b0355e32be7170ed1432d57a84', src: this.preview, caption: this.caption, copyright: this.copyright, ratio: this.currentlyFullscreen ? Ratio.WIDE : Ratio.HD, fullscreen: this.currentlyFullscreen || (this.mobileFullscreenPreview && !this.showVideo), transparency: this.showVideo && !this.currentlyFullscreen ? 1 : 0, saturation: this.showVideo ? 0.7 : 0, blurred: this.showVideo ? 1 : 0, ref: (element) => { this.imageElement = element; }, showCaption: !!this.caption, showCopyright: !!this.copyright }), h("div", { key: '22eb9a8eb219d5fce8db2b7a4769529ed7e258d1', class: "content", ref: (element) => { this.contentElement = element; } }, h("div", { key: '2fc77cebb3541833ef1bf43ac159b7421a98241e', class: containerClasses, ref: (element) => { this.videoContainer = element; } }, h("div", { key: 'e3e264fa59333c007765488397fe92d242164ef2' })), h("div", { key: '14c2409e9703aad624ff397e74b0e64f8753cc49', class: overlayClasses }, h("roche-icon", { key: '822b909f569aa2415f5c0fe0398ed5209e2c50b9', icon: "play", height: iconHeight, onClick: this.handlePlay }))), h("roche-icon", { key: 'd5197dfb89cbd0d6a4494110af356c69b570001a', class: closeClasses, icon: "close", height: `${remCalc(24)}rem`, onClick: this.handlePause })));
    }
    handleResize() {
        this.currentlyFullscreen = (this.fullscreen && breakpoints.medium <= window.innerWidth);
        const { imageElementHeight } = this.imageElement;
        const videoWidth = this.element.getBoundingClientRect().width / imageElementHeight < RATIO_HD
            ? 'width:100%'
            : `width:${(imageElementHeight - MINIMUM_VIDEO_PADDING * 2) * RATIO_HD}px; padding:${remCalc(MINIMUM_VIDEO_PADDING)} 0`;
        // Set the new height of the video container according to the image height
        // This is needed in order not to cause a potential render-loop
        this.contentElement.setAttribute('style', `height:${this.videoloop ? this.videoForcedHeight : imageElementHeight}px`);
        this.videoContainer.setAttribute('style', this.currentlyFullscreen ? videoWidth : 'width:100%');
        if (this.videoType === VideoType.YOUTUBE && this.videoObject) {
            const { width } = this.videoContainer.getBoundingClientRect();
            this.videoObject.setSize(width, (width * 9) / 16);
            this.videoContainer.setAttribute('style', 'display: flex');
        }
    }
    async handlePlay() {
        if (this.initStatus === 'not initialized') {
            await this.init();
        }
        if (this.initStatus === 'initializing') {
            await this.initialized;
        }
        if (!this.videoObject) {
            // Do not start playing if video is not loaded yet
            return;
        }
        if (!this.videoloop) {
            window.dispatchEvent(new CustomEvent('rocheVideoPlayerPlay', { detail: this }));
        }
        this.showVideo = true;
        if (this.videoType === VideoType.YOUTUBE) {
            this.videoObject.playVideo();
        }
        else {
            this.videoObject.player.play();
        }
        this.videoReady = true;
    }
    handlePause() {
        this.showVideo = false;
        if (this.videoType === VideoType.YOUTUBE) {
            this.videoObject.pauseVideo();
            this.videoObject.seekTo(0);
        }
        else {
            this.videoObject.player.pause();
            this.videoObject.player.currentTime(0);
        }
        this.closedEvent.emit(this);
    }
    brightcoveVideoTimeupdateEvent() {
        var _a;
        const videoObject = (_a = this.videoObject) === null || _a === void 0 ? void 0 : _a.player;
        videoObject === null || videoObject === void 0 ? void 0 : videoObject.on('timeupdate', () => {
            if (this.videoPercentage < Percentages.TEN) {
                return;
            }
            this.declareTrackingVideoPercentage(this.videoPercentage);
        });
    }
    brightcoveVideoPauseEvent() {
        var _a;
        const videoObject = (_a = this.videoObject) === null || _a === void 0 ? void 0 : _a.player;
        videoObject === null || videoObject === void 0 ? void 0 : videoObject.on('pause', () => {
            if (videoObject.seeking()) {
                this.isSeeking = true;
                return;
            }
            this.track(Object.assign(Object.assign({}, this.defaultTracking), { value: 'pause' }));
        });
    }
    brightcoveVideoPlayEvent() {
        var _a;
        const videoObject = (_a = this.videoObject) === null || _a === void 0 ? void 0 : _a.player;
        videoObject === null || videoObject === void 0 ? void 0 : videoObject.on('playing', () => {
            if (this.isSeeking) {
                this.isSeeking = false;
                return;
            }
            this.track(Object.assign(Object.assign({}, this.defaultTracking), { value: 'play' }));
            if (!this.videoloop) {
                AdobeTracking(Object.assign(Object.assign({}, this.defaultAdobeTracking), { autoplay: this.videoloop ? this.videoloop : false }), 'videoStart');
            }
        });
    }
    isPercentageAlreadyTracked(key) {
        if (this.trackedVideoPercentages[key]) {
            return true;
        }
        this.trackedVideoPercentages[key] = true;
        return false;
    }
    getBrightcoveVideoUrl() {
        var _a, _b;
        const playerBrightcove = (_a = this.videoObject) === null || _a === void 0 ? void 0 : _a.player;
        if (playerBrightcove) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const videoId = (_b = playerBrightcove.mediainfo) === null || _b === void 0 ? void 0 : _b.id;
            const { brightcoveAccountId, brightcovePlayerId } = DomService.getGlobalConfig();
            const brightcoveUrl = `https://players.brightcove.net/${brightcoveAccountId}/${brightcovePlayerId}_default/index.html?videoId=${videoId}`;
            return brightcoveUrl;
        }
        return '';
    }
    get videoUrl() {
        return this.videoType === VideoType.YOUTUBE
            ? `https://www.youtube.com/watch?v=${this.youtubeVideo}`
            : this.getBrightcoveVideoUrl();
    }
    get totalVideoLength() {
        return this.videoType === 'youtube'
            ? Math.round(this.videoObject.getDuration())
            : this.videoObject.player.duration();
    }
    get defaultAdobeTracking() {
        return {
            name: this.caption,
            url: this.videoUrl,
            provider: this.videoType,
            durationTotal: this.totalVideoLength,
        };
    }
    trackVideoPercentage(videoPercentage) {
        if (this.isPercentageAlreadyTracked(videoPercentage)) {
            return;
        }
        if (videoPercentage >= Percentages.TWENTY_FIVE) {
            this.track(Object.assign(Object.assign({}, this.defaultTracking), { value: 'video_progress', additionalParameters: {
                    videoId: this.video,
                    video_title: this.caption,
                    video_percentage: videoPercentage,
                } }));
        }
        if (videoPercentage <= Percentages.SEVENTY_FIVE && !this.videoloop) {
            AdobeTracking(Object.assign(Object.assign({}, this.defaultAdobeTracking), { progress: videoPercentage }), 'videoProgress');
        }
    }
    track(trackingData) {
        if (this.videoloop) {
            return;
        }
        TrackingService.push(trackingData);
    }
    /* ERROR HANDLING */
    handleYoutubeError(error) {
        const errorCode = (error === null || error === void 0 ? void 0 : error.data) || 'YOUTUBE_UNKNOWN_ERROR';
        this.trackVideoError(errorCode);
    }
    handleBrightcoveError(error) {
        var _a, _b, _c;
        // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
        const errorCode = ((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.target) === null || _a === void 0 ? void 0 : _a.player) === null || _b === void 0 ? void 0 : _b.error_) === null || _c === void 0 ? void 0 : _c.code) || 'BRIGHTCOVE_UNKNOWN_ERROR';
        this.trackVideoError(errorCode);
    }
    trackVideoError(errorCode) {
        AdobeTracking(Object.assign(Object.assign({}, this.defaultAdobeTracking), { error: errorCode }), 'videoError');
    }
    get element() { return this; }
    static get style() { return RocheVideoPlayerStyle0; }
}, [0, "roche-video-player", {
        "preview": [1],
        "youtubeVideo": [1, "youtube-video"],
        "brightcoveVideo": [1, "brightcove-video"],
        "videoloop": [4],
        "caption": [1],
        "copyright": [1],
        "fullscreen": [4],
        "mobileFullscreenPreview": [4, "mobile-fullscreen-preview"],
        "componentWidth": [2, "component-width"],
        "videoReady": [1540, "video-ready"],
        "videoForcedHeight": [2, "video-forced-height"],
        "showVideo": [32],
        "currentlyFullscreen": [32]
    }, [[8, "rocheVideoPlayerPlay", "handleHide"]]]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-video-player", "roche-icon", "roche-image", "roche-video-player"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-video-player":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheVideoPlayer);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "roche-image":
            if (!customElements.get(tagName)) {
                defineCustomElement();
            }
            break;
        case "roche-video-player":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

const rocheImageCss = ":host{display:block;margin-top:3rem;--responsive-container:1672px, 1440px, 1024px, 768px, 320px, 100px;-ie-responsive-container:1672px, 1440px, 1024px, 768px, 320px, 100px;width:100%}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}:host .image-container{margin:0}:host .image{position:relative;width:100%;background-size:cover;background-repeat:no-repeat;overflow:hidden}:host .image .filters{pointer-events:none}:host .image .filters .brightness,:host .image .filters .saturation,:host .image .filters .blur{position:absolute;top:0;left:0;width:100%;height:100%}:host .image .filters .brightness{background-color:#fff}:host .image .filters .saturation{z-index:200;background-color:#000}:host .image .filters .blur{background-position:center center;background-size:cover;background-repeat:no-repeat;filter:blur(10px)}:host .image img{width:100%;height:100%;object-fit:cover}@media print{:host .image img{opacity:1;height:31.25rem;width:18.75rem;object-fit:contain}}:host .image video{width:100%}:host .image roche-video-player{margin-top:0;pointer-events:none;opacity:0}:host .image roche-video-player[video-ready]{opacity:1;transition:1s;transition-delay:1s}:host .image roche-video-player video-js [class^=vjs-]:not(video){display:none}:host .image--fullscreen{width:100vw;margin-left:calc(50% - 50vw)}:host .image--bleed{width:100vw;margin-left:calc(50% - 50vw)}@media (min-width: 90rem){:host .image--bleed{width:calc(1552px + 20%);margin-left:calc((-1552px - 20%) / 12)}}:host .image--original{overflow:auto;background-size:contain}:host .image--original img{height:auto;display:block}:host .image--top{background-position:top}:host .image--center{background-position:center}:host .image--bottom{background-position:bottom}:host .description{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:0.875rem;font-weight:400;line-height:1.1875rem;color:var(--color-text--secondary);margin-top:0.75rem;max-width:47rem;color:var(--color-interactive--secondary-idle)}@media (min-width: 48rem){:host .description{font-size:1rem;line-height:1.3125rem}}@media (min-width: 64rem){:host .description{max-width:55rem}}@media (min-width: 90rem){:host .description{max-width:60rem}}:host .caption{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--body-small--font-size--small-up);line-height:1.625rem;color:#544f4f}@media (min-width: 48rem){:host .caption{font-size:var(--body-small--font-size--medium-up);line-height:1.8125rem}}:host .copyright{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:0.875rem;font-weight:400;line-height:1.1875rem;color:var(--color-text--secondary);margin-top:0.25rem;color:#9d948e}@media (min-width: 48rem){:host .copyright{font-size:1rem;line-height:1.3125rem}}:host([dir=rtl]) .image--bleed{margin-right:calc(50% - 50vw);margin-left:0}@media (min-width: 90rem){:host([dir=rtl]) .image--bleed{margin-right:calc((-1552px - 20%) / 12);margin-left:0}}";
const RocheImageStyle0 = rocheImageCss;

const RocheImage = /*@__PURE__*/ proxyCustomElement(class RocheImage extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.imageHeightUpdated = createEvent(this, "imageHeightUpdated", 7);
        this.triggerImageHeightUpdated = () => {
            this.imageHeightUpdated.emit(this.componentHeight);
        };
        this.src = undefined;
        this.brightcoveId = undefined;
        this.caption = undefined;
        this.copyright = undefined;
        this.alt = undefined;
        this.ratio = Ratio.HD;
        this.position = Position.CENTER;
        this.transparency = 0;
        this.brightness = 0;
        this.saturation = 0;
        this.blurred = 0;
        this.fullscreen = false;
        this.bleed = false;
        this.showCaption = false;
        this.showCopyright = false;
        this.componentSize = null;
        this.forceHeight = undefined;
        this.widthGetterAlgorithm = WidthGetterAlgorithm.WITH_TRANSFORM;
        this.imageWidth = null;
        this.componentHeight = 500;
    }
    get imageAttributes() {
        const parsed = StringService.stringToObject(this.src);
        const src = typeof parsed === 'string' ? parsed : (parsed || {}).src;
        /**
         * If a dimension is set to `0`, the Storyblok image service will pick a
         * size proportional to the other dimension. If both are zero, the original
         * image will be returned. See https://www.storyblok.com/docs/image-service
         *
         * Usually, we do not explicitly set the height parameter (`$1`) as this
         * allows the user to choose a focus-point via the `position` prop.
         * If, however, the `ratio` chosen by the user is smaller than the intrinsic
         * ratio of the original image, this would lead to a loss of quality with
         * the height dimension of the image being stretched-out. In that case, the
         * height parameter needs to be set.
         *
         * Note that the final `imageWidth` is not available in the first render.
         * In order to prevent unnecessary network requests, the `replacedSrc` is
         * set to an empty string until the `imageWidth` is known.
         */
        const [width, height] = this.ratioParsed.split(':').map(parseFloat);
        const shouldSetHeight = RocheImage.intrinsicImageRatio(src) > (width / height);
        const replacedSrc = ((this.imageWidth !== null && src) || '')
            .replace(/\$0/g, `${this.imageWidth || 0}`)
            .replace(/\$1/g, `${(shouldSetHeight && this.imageHeight) || 0}`);
        return typeof parsed === 'string' ? {
            src: replacedSrc,
            caption: this.caption,
            copyright: this.copyright,
            alt: this.alt,
        } : {
            src: replacedSrc,
            caption: (this === null || this === void 0 ? void 0 : this.caption) || (parsed === null || parsed === void 0 ? void 0 : parsed.caption),
            copyright: (this === null || this === void 0 ? void 0 : this.copyright) || (parsed === null || parsed === void 0 ? void 0 : parsed.copyright),
            alt: (this === null || this === void 0 ? void 0 : this.alt) || (parsed === null || parsed === void 0 ? void 0 : parsed.alt),
        };
    }
    get isVideo() {
        return !!this.brightcoveId;
    }
    get ratioParsed() {
        const index = ratioValues.indexOf(this.ratio);
        return index >= 0 ? Ratio[ratioKeys[index]] : Ratio.HD;
    }
    get positionParsed() {
        const index = positionValues.indexOf(this.position);
        return index >= 0 ? Position[positionKeys[index]] : Position.CENTER;
    }
    /**
     * Get the intrinsic ratio of the original image.
     *
     * Example:
     *
     *                Original image dimensions ─┐
     *                                       ┌───┴───┐
     * https://assets.cwp.roche.com/f/144169/2000x1000/f3ed8cbd49/image.jpg/m/1672x0/filters:format(webp):quality(90)/
     *                                       └───┬───┘
     * intrinsicImageRatio('https://ass...') === 2
     */
    static intrinsicImageRatio(src) {
        var _a;
        if (!src) {
            return null;
        }
        const dimensionRegex = /(\d+)x(\d+)/;
        const [, width, height] = (_a = src.match(dimensionRegex)) !== null && _a !== void 0 ? _a : [];
        return width && height ? (parseFloat(width) / parseFloat(height)) : null;
    }
    get imageHeight() {
        const [width, height] = this.ratioParsed.split(':').map(parseFloat);
        return width && height ? Math.floor((this.imageWidth * height) / width) : 0;
    }
    updateSize() {
        var _a;
        if (this.componentSize === null) {
            return;
        }
        const index = this.sizes.findIndex((size) => size === this.componentSize);
        this.imageWidth = (_a = this.sizes[index < 0 ? this.sizes.length - 1 : Math.max(0, index - 1)]) !== null && _a !== void 0 ? _a : null;
        this.updateHeight();
    }
    updateHeight() {
        if (!this.imageElement) {
            return;
        }
        const elementWidth = this.widthGetterAlgorithm === WidthGetterAlgorithm.WITH_TRANSFORM
            ? this.imageElement.getBoundingClientRect().width
            : this.imageElement.offsetWidth;
        const [width, height] = this.ratioParsed.split(':').map(parseFloat);
        this.componentHeight = width && height ? Math.floor((elementWidth * height) / width) : 0;
    }
    componentWillLoad() {
        // Expose the image height so that other components can overlay anything correctly on top
        Object.defineProperty(this.element, 'imageElementHeight', {
            get: (function getter() {
                var _a;
                return (_a = this.imageElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height;
            }).bind(this),
        });
        // Expose reinitialize. Check HTMLRocheImageElementExtended types for details
        this.element.reinitialize = this.componentDidLoad.bind(this);
    }
    componentDidLoad() {
        this.sizes = getElementSizes(this.element);
        this.updateSize();
        responsive(this.element, 'component-size', this.imageElement, debounce_1.debounce(this.updateHeight.bind(this), 50));
        DomService.handleTextDirection(this.element);
    }
    componentDidRender() {
        this.triggerImageHeightUpdated();
    }
    render() {
        const { src, caption, copyright, alt, } = this.imageAttributes;
        const imageClasses = {
            image: true,
            'image--fullscreen': this.fullscreen,
            'image--bleed': this.bleed,
            'image--top': this.positionParsed === Position.TOP,
            'image--center': this.positionParsed === Position.CENTER,
            'image--bottom': this.positionParsed === Position.BOTTOM,
            'image--original': this.ratio === Ratio.ORIGINAL,
        };
        const imageStyle = Object.assign(Object.assign({}, (!!this.componentHeight && {
            height: `${this.forceHeight || this.componentHeight}px`,
        })), { opacity: `${1 - this.transparency}` });
        return (h("figure", { key: 'e213e84992b201b9bf7bce96329460d10e0bdaea', class: "image-container" }, h("div", { key: '2302b8bfedd6da1d66f7b039aff8f97557216037', class: imageClasses, style: imageStyle, ref: (element) => { this.imageElement = element; } }, h("div", { key: 'b2e7f5d74d8d81613a2dc55160a69e55d2e08926', class: "filters" }, h("div", { key: '4def23e59bfad7abeae1b0539af82165f7ce221b', class: "saturation", style: { opacity: `${this.saturation}` } }), h("div", { key: '7ea533d859eee97757f13d3cab0d82c574a9cb9e', class: "brightness", style: { opacity: `${this.brightness}` } })), !this.isVideo ? (h("img", { src: src, alt: alt, title: caption, loading: "lazy" })) : (h("roche-video-player", { "brightcove-video": this.brightcoveId, videoloop: true, "video-forced-height": this.forceHeight || this.componentHeight }))), ((this.showCaption && caption) || (this.showCopyright && copyright)) && (h("div", { class: "description" }, this.showCaption && caption
            && h("figcaption", { class: "caption" }, caption), this.showCopyright && copyright
            && h("div", { class: "copyright" }, "\u00A9 ", copyright)))));
    }
    get element() { return this; }
    static get watchers() { return {
        "componentSize": ["updateSize"],
        "ratio": ["updateHeight"]
    }; }
    static get style() { return RocheImageStyle0; }
}, [1, "roche-image", {
        "src": [1],
        "brightcoveId": [513, "brightcove-id"],
        "caption": [1],
        "copyright": [1],
        "alt": [1],
        "ratio": [1],
        "position": [1],
        "transparency": [2],
        "brightness": [2],
        "saturation": [2],
        "blurred": [2],
        "fullscreen": [4],
        "bleed": [4],
        "showCaption": [4, "show-caption"],
        "showCopyright": [4, "show-copyright"],
        "componentSize": [2, "component-size"],
        "forceHeight": [2, "force-height"],
        "widthGetterAlgorithm": [2, "width-getter-algorithm"],
        "imageWidth": [32],
        "componentHeight": [32]
    }, undefined, {
        "componentSize": ["updateSize"],
        "ratio": ["updateHeight"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-image", "roche-icon", "roche-image", "roche-video-player"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-image":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheImage);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "roche-image":
            if (!customElements.get(tagName)) {
                defineCustomElement();
            }
            break;
        case "roche-video-player":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

export { RocheImage as R, defineCustomElement as a, RocheVideoPlayer as b, defineCustomElement$1 as d };

//# sourceMappingURL=roche-image2.js.map